import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import { BorderOutlined, CheckSquareOutlined, DeleteOutlined } from '@ant-design/icons';
import ChevronLeftSvg from '../../../assets/icons/chevron-left.svg?react';
import InboxActionsSvg from '../../../assets/icons/inbox-actions.svg?react';
import InboxUpdatesSvg from '../../../assets/icons/inbox-updates.svg?react';
import InboxFilterSvg from '../../../assets/icons/inbox-filter.svg?react';
import InboxOptionsSvg from '../../../assets/icons/inbox-options.svg?react';
import { NotificationTab, NotificationFilter, Notification } from '../../../types/Notification';
import { DeleteNotificationsModal, DeleteNotificationsMode } from '../../Modal/DeleteNotificationsModal';
import {
  useInboxBulkMutation,
  useInboxNotifications,
  useNotificationActionMutation,
} from '../../../serverState/organization';
import { NotificationCard } from './NotificationCard';
import { InboxMenu } from './InboxMenu';
import { saveInboxFilter, loadInboxFilter, clearInboxFilter } from '../../../common/localStorage';
import { TasksZeroState } from '../../UI/TasksZeroState';

// Both tabs open on "all" so a fully-read / fully-completed inbox still shows its
// notifications (unread ones keep the orange dot). "all" is the default state that
// drives the Filter trigger's color indicator (AC10).
const DEFAULT_FILTER: Record<NotificationTab, NotificationFilter> = { actions: 'all', updates: 'all' };
// The Inbox always opens on Actions — the tab is never remembered between opens (#2867).
const DEFAULT_TAB: NotificationTab = 'actions';

const FILTER_OPTIONS: Record<NotificationTab, { value: NotificationFilter; label: string }[]> = {
  actions: [
    { value: 'notdone', label: 'To do' },
    { value: 'done', label: 'Completed' },
    { value: 'all', label: 'All actions' },
  ],
  updates: [
    { value: 'unread', label: 'Unread' },
    { value: 'read', label: 'Read' },
    { value: 'all', label: 'All updates' },
  ],
};

type BulkAction =
  | 'markAllRead'
  | 'markAllUnread'
  | 'markAllDone'
  | 'markAllUndone'
  | 'deleteAllRead'
  | 'deleteAllCompleted';

const OPTIONS_MENU_ITEMS: Record<NotificationTab, { value: BulkAction; label: string; icon: React.ReactNode }[]> = {
  actions: [
    // The "mark all" pair leads with the completing action on both tabs, so Completed
    // sits first here just as "Mark all as read" does under Updates (#3008).
    { value: 'markAllDone', label: 'Mark all as Completed', icon: <CheckSquareOutlined /> },
    { value: 'markAllUndone', label: 'Mark all as To do', icon: <BorderOutlined /> },
    { value: 'deleteAllCompleted', label: 'Delete completed', icon: <DeleteOutlined /> },
  ],
  updates: [
    { value: 'markAllRead', label: 'Mark all as read', icon: <CheckSquareOutlined /> },
    // Second in order, mirroring the Completed / To do pair the Actions tab has (#3006).
    { value: 'markAllUnread', label: 'Mark all as unread', icon: <BorderOutlined /> },
    { value: 'deleteAllRead', label: 'Delete read', icon: <DeleteOutlined /> },
  ],
};

export interface InboxSidebarPanelProps {
  organizationId: number;
  collapsed: boolean;
  onBack: () => void;
  onOpenNotification: (n: Notification) => void;
  selectedNotificationId?: number;
  spaceId?: number;
}

export const InboxSidebarPanel: React.FC<InboxSidebarPanelProps> = ({
  organizationId,
  collapsed,
  onBack,
  onOpenNotification,
  selectedNotificationId,
  spaceId,
}) => {
  const resolveFilter = (t: NotificationTab): NotificationFilter => {
    if (spaceId == null) return DEFAULT_FILTER[t];
    const stored = loadInboxFilter(spaceId, t);
    const valid = FILTER_OPTIONS[t].some((o) => o.value === stored);
    return valid ? (stored as NotificationFilter) : DEFAULT_FILTER[t];
  };

  const [tab, setTab] = useState<NotificationTab>(DEFAULT_TAB);
  const [filter, setFilter] = useState<NotificationFilter>(() => resolveFilter(DEFAULT_TAB));
  // Bulk deletes are irreversible, so they route through a confirm step (#2820).
  const [pendingDelete, setPendingDelete] = useState<DeleteNotificationsMode | null>(null);

  // The org-wide inbox can stay mounted across a space switch (e.g. opening a
  // cross-space notification calls setSelectedSpace without closing the panel),
  // so re-resolve the remembered filter for the newly selected space.
  useEffect(() => {
    setFilter(resolveFilter(tab));
    // Only resync on space change; resolveFilter/tab are intentionally omitted.
  }, [spaceId]);

  const query = useInboxNotifications(organizationId, tab, filter);
  const actionMutation = useNotificationActionMutation(organizationId);
  const bulkMutation = useInboxBulkMutation(organizationId);

  const handleOpen = (n: Notification) => {
    // Updates cards mark read on open; Actions cards stay unread (read state is
    // driven by the To do / Completed toggle there, not by opening the task).
    if (tab === 'updates' && !n.read) actionMutation.mutate({ id: n.id, action: 'read' });
    onOpenNotification(n);
  };

  const firstPage = query.data?.pages[0];
  const counts = firstPage?.counts ?? { actions: 0, updates: 0 };
  const items = query.data?.pages.flatMap((p) => p?.items ?? []) ?? [];

  const switchTab = (next: NotificationTab) => {
    setTab(next);
    setFilter(resolveFilter(next));
  };

  const TabButton = ({
    id,
    label,
    count,
    Icon,
  }: {
    id: NotificationTab;
    label: string;
    count: number;
    Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  }) => {
    const active = tab === id;
    return (
      <div
        onClick={() => switchTab(id)}
        // Left-aligned, not centred: centring re-positions the title every time the
        // count badge appears or disappears, so the tab label visibly jumps (#3007).
        className={`flex flex-1 items-center justify-start py-1.5 cursor-pointer border-0 border-b-2 border-solid border-transparent ${
          active ? 'border-b-taskonioGreen-600' : ''
        }`}
      >
        <div className="flex items-center gap-1 px-2 py-1 rounded">
          <Icon className={`w-4 h-4 shrink-0 ${active ? 'text-neutral-1000' : 'text-neutral-900'}`} />
          <span className={`text-sm leading-5 font-medium ${active ? 'text-neutral-1000' : 'text-neutral-900'}`}>
            {label}
          </span>
          {count > 0 && (
            <span className="flex items-center justify-center min-w-[16px] h-4 px-1 rounded-lg bg-neutral-800 text-white text-xs font-semibold leading-none">
              {count}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="flex items-center justify-between px-4">
        <div className="flex items-center gap-1 cursor-pointer" onClick={onBack}>
          <ChevronLeftSvg width={16} className="text-neutral-900" />
          {!collapsed && <span className="text-neutral-900 text-sm font-medium">Back</span>}
        </div>
        {!collapsed && (
          <div className="flex items-center gap-1">
            <InboxMenu
              triggerLabel="Filter"
              triggerIcon={<InboxFilterSvg className="w-4 h-4" />}
              triggerActive={filter !== DEFAULT_FILTER[tab]}
              options={FILTER_OPTIONS[tab]}
              selected={filter}
              onSelect={(value) => {
                setFilter(value);
                if (spaceId != null) saveInboxFilter(spaceId, tab, value);
              }}
              onReset={() => {
                setFilter(DEFAULT_FILTER[tab]);
                if (spaceId != null) clearInboxFilter(spaceId, tab);
              }}
            />
            <InboxMenu
              triggerLabel="More actions"
              triggerIcon={<InboxOptionsSvg className="w-4 h-4" />}
              options={OPTIONS_MENU_ITEMS[tab]}
              onSelect={(action) => {
                if (action === 'deleteAllCompleted' || action === 'deleteAllRead') {
                  setPendingDelete(action);
                  return;
                }
                bulkMutation.mutate({ action });
              }}
            />
          </div>
        )}
      </div>

      {!collapsed && (
        <div className="flex items-center border-0 border-b border-solid border-neutral-300 px-4">
          <TabButton id="actions" label="Actions" count={counts.actions} Icon={InboxActionsSvg} />
          <TabButton id="updates" label="Updates" count={counts.updates} Icon={InboxUpdatesSvg} />
        </div>
      )}

      <div className="flex-1 overflow-y-auto flex flex-col gap-2 p-2">
        {items.length === 0 ? (
          <TasksZeroState title="You are all caught up!" hideCta fullHeight={false} />
        ) : (
          items.map((n) => (
            <NotificationCard
              key={n.id}
              notification={n}
              selected={n.id === selectedNotificationId}
              // Actions never track `read` (opening one must not mark it read), so there the dot
              // marks the still-open ones — the same state the tab badge counts. Updates keep
              // the unread dot, which is what their badge counts (#2971).
              showNewDot={tab === 'actions' ? !n.done : !n.read}
              newDotColor={tab === 'actions' ? 'bg-blue-600' : 'bg-orange-500'}
              onClick={() => handleOpen(n)}
              onDone={
                tab === 'actions'
                  ? () =>
                      actionMutation.mutate(
                        { id: n.id, action: n.done ? 'undone' : 'done' },
                        {
                          onSuccess: () => void message.success(n.done ? 'Action moved to To do' : 'Action completed'),
                        }
                      )
                  : undefined
              }
              onToggleRead={
                tab === 'updates'
                  ? () => actionMutation.mutate({ id: n.id, action: n.read ? 'unread' : 'read' })
                  : undefined
              }
              onDelete={() => actionMutation.mutate({ id: n.id, action: 'delete' })}
            />
          ))
        )}
      </div>

      <DeleteNotificationsModal
        open={pendingDelete !== null}
        // Keep the last mode while the modal animates closed so the title doesn't
        // flip to the other tab's copy on the way out.
        mode={pendingDelete ?? 'deleteAllRead'}
        confirmLoading={bulkMutation.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (pendingDelete) bulkMutation.mutate({ action: pendingDelete });
          setPendingDelete(null);
        }}
      />
    </div>
  );
};
